import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import customAppointmentTypesApi from '../utils/api/customAppointmentTypesApi';
import { appointmentTypeCatalogKeys } from '../utils/queryClient';
import {
  DEFAULT_HIDDEN_PRESET_KEYS,
  fetchPresetVisibility,
} from '../utils/appointmentTypePresetVisibility';

const AppointmentTypeCatalogContext = createContext(null);

export function AppointmentTypeCatalogProvider({ children, enabled = true }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const catalogEnabled = enabled && isAuthenticated;

  const customTypesQuery = useQuery({
    queryKey: appointmentTypeCatalogKeys.customTypes(),
    queryFn: () => customAppointmentTypesApi.getAll(true),
    enabled: catalogEnabled,
    staleTime: 1000 * 60 * 5,
  });

  const presetVisibilityQuery = useQuery({
    queryKey: appointmentTypeCatalogKeys.presetVisibility(),
    queryFn: fetchPresetVisibility,
    enabled: catalogEnabled,
    staleTime: 1000 * 60 * 5,
    placeholderData: DEFAULT_HIDDEN_PRESET_KEYS,
    retry: false,
  });

  const customTypes = useMemo(
    () => (catalogEnabled && Array.isArray(customTypesQuery.data) ? customTypesQuery.data : []),
    [catalogEnabled, customTypesQuery.data]
  );
  const hiddenPresetKeys = catalogEnabled ? presetVisibilityQuery.data ?? DEFAULT_HIDDEN_PRESET_KEYS : DEFAULT_HIDDEN_PRESET_KEYS;

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: appointmentTypeCatalogKeys.customTypes() }),
      queryClient.invalidateQueries({ queryKey: appointmentTypeCatalogKeys.presetVisibility() }),
    ]);
  }, [queryClient]);

  const loading = catalogEnabled && customTypesQuery.isLoading;
  const error = catalogEnabled ? customTypesQuery.error || null : null;

  const value = useMemo(() => ({
    customTypes,
    hiddenPresetKeys,
    loading,
    error,
    refresh,
  }), [customTypes, hiddenPresetKeys, loading, error, refresh]);

  return (
    <AppointmentTypeCatalogContext.Provider value={value}>
      {children}
    </AppointmentTypeCatalogContext.Provider>
  );
}

export function useAppointmentTypeCatalogContext() {
  return useContext(AppointmentTypeCatalogContext);
}
